import { contrastRatio, HexToRGBA, parseHex, RGBAtoCSS } from "vuetify/lib/util/colorUtils";

// The background a chip sits on, per theme (light = white page/cards, dark = card surface #312d4b).
const THEME_BG = {
  light: { r: 255, g: 255, b: 255 },
  dark: { r: 49, g: 45, b: 75 },
};

// Minimum contrast a label color needs against the theme background before it's nudged to be readable.
const MIN_CONTRAST = 3;

const mix = (color, target, amount) => ({
  r: Math.round(color.r + (target.r - color.r) * amount),
  g: Math.round(color.g + (target.g - color.g) * amount),
  b: Math.round(color.b + (target.b - color.b) * amount),
});

/**
 * Returns the label color nudged toward white (dark theme) or black (light
 * theme) just enough to be readable on the theme background, keeping its hue.
 * Colors that already contrast enough are returned untouched.
 */
const toReadableColor = (item, isDark) => {
  const color = HexToRGBA(parseHex(item?.color ?? "#000000"));
  const background = isDark ? THEME_BG.dark : THEME_BG.light;
  const target = isDark ? { r: 255, g: 255, b: 255 } : { r: 0, g: 0, b: 0 };

  let result = color;

  for (let amount = 0.1; amount <= 1 && contrastRatio(result, background) < MIN_CONTRAST; amount += 0.1) {
    result = mix(color, target, amount);
  }

  return result;
};

/**
 * Semi-transparent background color for a label chip, adjusted so the tint
 * stays visible in the current theme.
 * @param {Object} item - Object with a `color` property in hex format (e.g. "#836af9")
 * @param {Boolean} [isDark=false] - Whether the dark theme is active
 * @returns {String} An rgba() string
 */
export const getChipColor = (item, isDark = false) => RGBAtoCSS({ ...toReadableColor(item, isDark), a: 0.15 });

/**
 * Readable text color for a label chip, preserving the label's hue.
 * @param {Object} item - Object with a `color` property in hex format
 * @param {Boolean} [isDark=false] - Whether the dark theme is active
 * @returns {String} An rgba() string
 */
export const getChipTextColor = (item, isDark = false) => RGBAtoCSS({ ...toReadableColor(item, isDark), a: 1 });
